// The right panel's shared controls, as live's Parameters draw them: a group under its title, a
// group live opens on "+", a labelled row, a number taken on Enter or blur, a slider with its value,
// a colour (a swatch and its hex), and seconds. The clip, picture and title settings build on them.
import { ActionIcon, ColorPicker, NumberInput, Popover, Slider, Stack, Text, TextInput } from "@mantine/core";
import {
  ArrowUpDown,
  CaseLower,
  CaseUpper,
  Minus,
  Plus,
  Ruler,
  TextAlignCenter,
  TextAlignEnd,
  TextAlignStart,
} from "lucide-react";
import { useState, type KeyboardEvent, type ReactNode } from "react";

import {
  shadowBlurMax,
  shadowBlurMin,
  shadowOffsetXMax,
  shadowOffsetXMin,
  shadowOffsetYMax,
  shadowOffsetYMin,
  strokeWidthMax,
  strokeWidthMin,
  textBackgroundPadXMax,
  textBackgroundPadXMin,
  textBackgroundPadYMax,
  textBackgroundPadYMin,
  textBackgroundRadiusMax,
  textBackgroundRadiusMin,
} from "../../api/generated/zod";

import classes from "./editor.module.css";
import type { Shadow, Stroke } from "./look";
import type { TextAlign, TextBackground, TextCase } from "./text-style";
import { fromSeconds, toSeconds } from "./time";

/** A group of parameters under a 12px title, as live's right panel lays them out. */
export function ParamSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className={classes.paramSection}>
      <Text className={classes.paramTitle}>{title}</Text>
      <Stack gap={10}>{children}</Stack>
    </div>
  );
}

/**
 * One of live's groups that open on "+" and close on "−" (Stroke, Shadow, Chroma Key): closed,
 * only its title and "+"; open, its fields under its title and "−".
 */
export function ExtraSection(props: {
  title: string;
  open: boolean;
  onAdd: () => void;
  onRemove: () => void;
  children: ReactNode;
}) {
  const { title, open } = props;
  return (
    <div className={open ? classes.paramSection : undefined}>
      <div className={open ? classes.paramHeadRow : classes.paramFolded}>
        <Text className={classes.paramTitle}>{title}</Text>
        <ActionIcon
          variant="subtle"
          color="gray"
          size={24}
          aria-label={`${open ? "Remove" : "Add"} ${title}`}
          onClick={open ? props.onRemove : props.onAdd}
        >
          {open ? <Minus size={14} /> : <Plus size={14} />}
        </ActionIcon>
      </div>
      {open ? <Stack gap={10}>{props.children}</Stack> : null}
    </div>
  );
}

/** A row of live's Parameters: its name on the left, its fields on the right. */
export function LookRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className={classes.lookRow}>
      <Text className={classes.paramLabel}>{label}</Text>
      <div className={classes.lookFields}>{children}</div>
    </div>
  );
}

/**
 * A number of live's Transform, with its letter before it ("X", "W"), committed on Enter or when
 * it loses the focus; the field then shows the value the timeline kept.
 */
export function LookNumber(props: {
  label: string;
  tag: ReactNode;
  value: number;
  min?: number;
  /** The contract's top: a number past it is taken at it, so no save is refused for it. */
  max?: number;
  onCommit: (value: number) => void;
}) {
  const [revision, setRevision] = useState(0);
  const shown = Math.round(props.value);
  const commit = (raw: string) => {
    const typed = Number(raw.replace(/[^\d.-]/g, ""));
    const next = Math.min(props.max ?? Infinity, Math.max(props.min ?? -Infinity, typed));
    if (raw.trim() !== "" && Number.isFinite(next) && next !== shown) props.onCommit(next);
    setRevision((current) => current + 1);
  };
  return (
    <NumberInput
      key={`${shown}:${revision}`}
      aria-label={props.label}
      classNames={{ input: classes.lookInput, section: classes.lookTag }}
      leftSection={props.tag}
      defaultValue={shown}
      {...(props.min === undefined ? {} : { min: props.min })}
      clampBehavior="none"
      allowDecimal={false}
      hideControls
      onBlur={(event) => commit(event.currentTarget.value)}
      onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") commit(event.currentTarget.value);
      }}
    />
  );
}

/** One of live's parameter sliders: the label, the slider, and its value with its unit. */
export function LevelSlider(props: {
  label: string;
  /** No label beside it: its section's title says it (live's Volume). */
  bare?: boolean;
  /** The slider's name when its label alone is not enough (a background's Opacity). */
  spoken?: string;
  value: number;
  max: number;
  unit: string;
  /** The slider's step: 100 for milliseconds, else 1 unless given (live's 5 % on a background). */
  step?: number;
  /** Values marked on its track (our Volume's 100 %, spec volume-boost; live's sliders have none). */
  marks?: readonly number[];
  /** Its value amber: past what is safe (a sound boosted past 100 %). */
  warn?: boolean;
  /** Takes no change (a keyed sound's Volume with the playhead off the clip). */
  disabled?: boolean;
  onChange: (value: number) => void;
}) {
  return (
    <div className={classes.levelRow} data-bare={props.bare === true || undefined}>
      {props.bare === true ? null : <Text className={classes.paramLabel}>{props.label}</Text>}
      <Slider
        className={classes.levelSlider}
        min={0}
        max={props.max}
        step={props.step ?? (props.unit === "ms" ? 100 : 1)}
        value={Math.min(props.value, props.max)}
        label={null}
        thumbLabel={props.spoken ?? props.label}
        disabled={props.disabled === true}
        marks={props.marks?.map((value) => ({ value })) ?? []}
        onChange={props.onChange}
      />
      <Text className={classes.levelValue} data-warn={props.warn === true || undefined}>
        {props.value} {props.unit}
      </Text>
    </div>
  );
}

/**
 * Live's colour field: a swatch that opens a picker, and the colour's hex beside it, taken on
 * Enter or when it loses the focus (six hex digits; anything else shows the colour kept).
 */
export function ColorField(props: {
  label: string;
  value: string;
  onCommit: (hex: string) => void;
  /** Its picker in a portal (the default); inside the field where a popover holds it. */
  withinPortal?: boolean;
}) {
  const { label, value, onCommit } = props;
  const [revision, setRevision] = useState(0);
  const commit = (raw: string) => {
    const typed = raw.trim().toUpperCase();
    const hex = typed.startsWith("#") ? typed : `#${typed}`;
    if (/^#[0-9A-F]{6}$/.test(hex) && hex !== value.toUpperCase()) onCommit(hex);
    setRevision((current) => current + 1);
  };
  return (
    <div className={classes.colorField}>
      <Popover position="left-start" shadow="md" withinPortal={props.withinPortal ?? true}>
        <Popover.Target>
          <ActionIcon
            variant="default"
            size={28}
            className={classes.colorSwatch}
            aria-label={`Pick ${label}`}
          >
            <span className={classes.colorChip} style={{ backgroundColor: value }} />
          </ActionIcon>
        </Popover.Target>
        <Popover.Dropdown>
          <ColorPicker
            format="hex"
            defaultValue={value}
            onChangeEnd={(hex) => onCommit(hex.toUpperCase())}
          />
        </Popover.Dropdown>
      </Popover>
      <TextInput
        key={`${value}:${revision}`}
        aria-label={label}
        classNames={{ input: classes.lookInput }}
        defaultValue={value.toUpperCase()}
        spellCheck={false}
        onBlur={(event) => commit(event.currentTarget.value)}
        onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
          if (event.key === "Enter") commit(event.currentTarget.value);
        }}
      />
    </div>
  );
}

export interface SecondsFieldProps {
  label: string;
  ms: number;
  min?: number;
  max?: number;
  /** What the contract would refuse about this value, shown on the field. */
  error?: string | undefined;
  onCommit: (ms: number) => void;
}

/**
 * Seconds, committed on blur or Enter, so typing "1.5" is not clamped at "1". After a commit the
 * field shows the value the timeline kept (it may be clamped).
 */
export function SecondsField({ label, ms, min, max, error, onCommit }: SecondsFieldProps) {
  // Each commit shows the value the timeline kept, even when it kept the old one (a move onto
  // another clip): the field starts again from it.
  const [revision, setRevision] = useState(0);
  const commit = (value: string) => {
    const next = fromSeconds(value);
    if (next !== undefined && next !== ms) onCommit(next);
    setRevision((current) => current + 1);
  };
  return (
    <NumberInput
      key={`${ms}:${revision}`}
      label={label}
      classNames={{ root: classes.paramRow, label: classes.paramLabel, input: classes.paramInput }}
      defaultValue={toSeconds(ms)}
      {...(min === undefined ? {} : { min: toSeconds(min) })}
      {...(max === undefined ? {} : { max: toSeconds(max) })}
      step={0.1}
      decimalScale={1}
      clampBehavior="none"
      allowNegative={false}
      suffix=" s"
      error={error}
      onBlur={(event) => commit(event.currentTarget.value)}
      onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") commit(event.currentTarget.value);
      }}
    />
  );
}

/** Live's Stroke: its Color and its Width (live's pixels). */
export function StrokeFields(props: { stroke: Stroke; onChange: (stroke: Stroke) => void }) {
  const { stroke, onChange } = props;
  return (
    <>
      <LookRow label="Color">
        <ColorField label="Stroke color" value={stroke.color} onCommit={(color) => onChange({ ...stroke, color })} />
      </LookRow>
      <LookRow label="Width">
        <LookNumber
          label="Stroke width"
          tag={<ArrowUpDown size={12} aria-hidden />}
          value={stroke.width}
          min={strokeWidthMin}
          max={strokeWidthMax}
          onCommit={(width) => onChange({ ...stroke, width })}
        />
      </LookRow>
    </>
  );
}

/** Live's Shadow: its Offset (X, Y), Blur and Color. */
export function ShadowFields(props: { shadow: Shadow; onChange: (shadow: Shadow) => void }) {
  const { shadow, onChange } = props;
  const ruler = <Ruler size={12} aria-hidden />;
  return (
    <>
      <LookRow label="Offset">
        <LookNumber
          label="Shadow offset X"
          tag={ruler}
          value={shadow.offsetX}
          min={shadowOffsetXMin}
          max={shadowOffsetXMax}
          onCommit={(offsetX) => onChange({ ...shadow, offsetX })}
        />
        <LookNumber
          label="Shadow offset Y"
          tag={ruler}
          value={shadow.offsetY}
          min={shadowOffsetYMin}
          max={shadowOffsetYMax}
          onCommit={(offsetY) => onChange({ ...shadow, offsetY })}
        />
      </LookRow>
      <LookRow label="Blur">
        <LookNumber
          label="Shadow blur"
          tag={<ArrowUpDown size={12} aria-hidden />}
          value={shadow.blur}
          min={shadowBlurMin}
          max={shadowBlurMax}
          onCommit={(blur) => onChange({ ...shadow, blur })}
        />
      </LookRow>
      <LookRow label="Color">
        <ColorField label="Shadow color" value={shadow.color} onCommit={(color) => onChange({ ...shadow, color })} />
      </LookRow>
    </>
  );
}

/** One of live's small toggles (an alignment, a decoration, a case): pressed when on. */
export function Toggle(props: { label: string; on: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <ActionIcon
      variant="default"
      size={28}
      className={classes.flipButton}
      aria-label={props.label}
      aria-pressed={props.on}
      data-on={props.on}
      onClick={props.onClick}
    >
      {props.children}
    </ActionIcon>
  );
}

export const ALIGNS: readonly { value: TextAlign; label: string; icon: ReactNode }[] = [
  { value: "left", label: "Align left", icon: <TextAlignStart size={14} /> },
  { value: "center", label: "Align center", icon: <TextAlignCenter size={14} /> },
  { value: "right", label: "Align right", icon: <TextAlignEnd size={14} /> },
];

export const CASES: readonly { value: TextCase; label: string; icon: ReactNode }[] = [
  { value: "as_typed", label: "As typed", icon: <Minus size={14} /> },
  { value: "upper", label: "Uppercase", icon: <CaseUpper size={14} /> },
  { value: "lower", label: "Lowercase", icon: <CaseLower size={14} /> },
];

/** Live's Text Background: its Color, Opacity (0 to 100 %, by 5), Radius, Pad X and Pad Y. */
export function BackgroundFields(props: {
  background: TextBackground;
  onChange: (background: TextBackground) => void;
}) {
  const { background, onChange } = props;
  return (
    <>
      <LookRow label="Color">
        <ColorField
          label="Background color"
          value={background.color}
          onCommit={(color) => onChange({ ...background, color })}
        />
      </LookRow>
      <LevelSlider
        label="Opacity"
        spoken="Background opacity"
        value={Math.round(background.opacity * 100)}
        max={100}
        step={5}
        unit="%"
        onChange={(percent) => onChange({ ...background, opacity: percent / 100 })}
      />
      <LookRow label="Radius">
        <LookNumber
          label="Background radius"
          tag="R"
          value={background.radius}
          min={textBackgroundRadiusMin}
          max={textBackgroundRadiusMax}
          onCommit={(radius) => onChange({ ...background, radius })}
        />
      </LookRow>
      <LookRow label="Padding">
        <LookNumber
          label="Pad X"
          tag="X"
          value={background.padX}
          min={textBackgroundPadXMin}
          max={textBackgroundPadXMax}
          onCommit={(padX) => onChange({ ...background, padX })}
        />
        <LookNumber
          label="Pad Y"
          tag="Y"
          value={background.padY}
          min={textBackgroundPadYMin}
          max={textBackgroundPadYMax}
          onCommit={(padY) => onChange({ ...background, padY })}
        />
      </LookRow>
    </>
  );
}
